import React, { useState, KeyboardEvent } from 'react';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { cn } from '@/lib/utils';

/** Coerce to string so we never render an object as a React child (API may return objects e.g. { winId, winDescription }). */
function tagToDisplay(tag: unknown): string {
  if (tag == null) return '';
  if (typeof tag === 'string') return tag;
  if (typeof tag === 'number' || typeof tag === 'boolean') return String(tag);
  if (typeof tag === 'object') {
    const o = tag as Record<string, unknown>;
    return (o.winDescription ?? o.win_description ?? o.description ?? o.name ?? JSON.stringify(tag)) as string;
  }
  return String(tag);
}

interface TagInputProps {
  tags: (string | unknown)[];
  onAdd: (tag: string) => void;
  onRemove: (tag: string) => void;
  placeholder?: string;
  maxLength?: number;
  className?: string;
  disabled?: boolean;
}

export const TagInput: React.FC<TagInputProps> = ({
  tags,
  onAdd,
  onRemove,
  placeholder = 'Add tag...',
  maxLength = 100,
  className,
  disabled = false,
}) => {
  const [inputValue, setInputValue] = useState('');

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && inputValue.trim()) {
      e.preventDefault();
      const trimmed = inputValue.trim();
      if (trimmed.length > 0 && trimmed.length <= maxLength && !tags.includes(trimmed)) {
        onAdd(trimmed);
        setInputValue('');
      }
    } else if (e.key === 'Backspace' && !inputValue && tags.length > 0) {
      const lastTag = tags[tags.length - 1];
      onRemove(typeof lastTag === 'string' ? lastTag : tagToDisplay(lastTag));
    }
  };

  const handleAdd = () => {
    const trimmed = inputValue.trim();
    if (trimmed.length > 0 && trimmed.length <= maxLength && !tags.includes(trimmed)) {
      onAdd(trimmed);
      setInputValue('');
    }
  };

  return (
    <div className={cn('space-y-2', className)}>
      <div className="flex flex-wrap gap-2">
        {tags.map((tag, index) => {
          const display = tagToDisplay(tag);
          return (
            <span
              key={index}
              className="inline-flex items-center gap-1 px-2 py-1 bg-secondary text-secondary-foreground rounded-md text-sm"
            >
              {display}
              {!disabled && (
                <button
                  type="button"
                  onClick={() => onRemove(typeof tag === 'string' ? tag : display)}
                  className="hover:bg-secondary-foreground/20 rounded-full p-0.5"
                  aria-label={`Remove ${display}`}
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </span>
          );
        })}
      </div>
      {!disabled && (
        <div className="flex gap-2">
          <Input
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            maxLength={maxLength}
            className="flex-1"
          />
          <Button type="button" onClick={handleAdd} variant="outline" size="sm">
            Add
          </Button>
        </div>
      )}
    </div>
  );
};







